/**
 * Types and functions for working with 2d rays
 * @module
 */
import { Line2Side } from "./core";
import type { Ray2 } from "./ray2Types";
import type { Vec2 } from "./vec2";
import { vec2Dist, vec2Dot, vec2Result, vec2Sub } from "./vec2";

export function ray2New(originX: number = 0, originY: number = 0, dirX: number = 1, dirY: number = 0): Ray2 {
  return { originX, originY, dirX, dirY };
}

export function ray2Eval(originX: number, originY: number, dirX: number, dirY: number, t: number): Vec2 {
  vec2Result[0] = originX + dirX * t;
  vec2Result[1] = originY + dirY * t;
  return vec2Result;
}

export function ray2DotProject(
  originX: number,
  originY: number,
  dirX: number,
  dirY: number,
  x: number,
  y: number,
): number {
  const [dx, dy] = vec2Sub(x, y, originX, originY);
  return vec2Dot(dx, dy, dirX, dirY);
}

export function ray2Project(originX: number, originY: number, dirX: number, dirY: number, x: number, y: number): Vec2 {
  const dot = ray2DotProject(originX, originY, dirX, dirY, x, y);
  return ray2Eval(originX, originY, dirX, dirY, dot);
}

export function ray2PerpDistance(
  originX: number,
  originY: number,
  dirX: number,
  dirY: number,
  x: number,
  y: number,
): number {
  const [px, py] = ray2Project(originX, originY, dirX, dirY, x, y);
  return vec2Dist(px, py, x, y);
}

export function ray2Side(
  originX: number,
  originY: number,
  dirX: number,
  dirY: number,
  x: number,
  y: number,
  epsilon: number = 0,
): Line2Side {
  const cross = dirX * (y - originY) - dirY * (x - originX);
  if (cross > epsilon) {
    return Line2Side.LEFT;
  } else if (cross < -epsilon) {
    return Line2Side.RIGHT;
  }
  return Line2Side.COLLINEAR;
}
